import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { updateDomain } from '@state/slices/studySlice';
import { setShowDomainBox } from '@state/slices/uiSlice';

/**
 * DomainInspector (Gate 1 Foundation)
 *
 * Exposes computational domain definition:
 * - Domain Width (m) [along X axis]
 * - Domain Length (m) [along Z axis]
 * - Domain Height (m) [along Y axis]
 *
 * Distinctly presented as physical spatial bounds, NOT a computational mesh.
 */
export const DomainInspector: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeScenarioId = useAppSelector((state) => state.study.study.activeScenarioId);
  const activeScenario = useAppSelector(
    (state) => state.study.study.scenarios[activeScenarioId]
  );
  const domain = activeScenario?.cfdSetup?.domain;
  const showDomainBox = useAppSelector((state) => state.ui.showDomainBox ?? true);

  const width = domain?.width ?? 100;
  const length = domain?.length ?? 100;
  const height = domain?.height ?? 30;

  // Local text input states to allow smooth typing, clearing, and backspacing
  const [widthInput, setWidthInput] = useState<string>(width.toString());
  const [lengthInput, setLengthInput] = useState<string>(length.toString());
  const [heightInput, setHeightInput] = useState<string>(height.toString());

  useEffect(() => {
    setWidthInput(width.toString());
  }, [width, activeScenarioId]);

  useEffect(() => {
    setLengthInput(length.toString());
  }, [length, activeScenarioId]);

  useEffect(() => {
    setHeightInput(height.toString());
  }, [height, activeScenarioId]);

  const volume = width * length * height;

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setWidthInput(raw);
    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) {
      dispatch(updateDomain({ width: val }));
    }
  };

  const handleWidthBlur = () => {
    const val = parseFloat(widthInput);
    if (isNaN(val) || val <= 0) {
      setWidthInput(width.toString());
    }
  };

  const handleLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLengthInput(raw);
    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) {
      dispatch(updateDomain({ length: val }));
    }
  };

  const handleLengthBlur = () => {
    const val = parseFloat(lengthInput);
    if (isNaN(val) || val <= 0) {
      setLengthInput(length.toString());
    }
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setHeightInput(raw);
    const val = parseFloat(raw);
    if (!isNaN(val) && val > 0) {
      dispatch(updateDomain({ height: val }));
    }
  };

  const handleHeightBlur = () => {
    const val = parseFloat(heightInput);
    if (isNaN(val) || val <= 0) {
      setHeightInput(height.toString());
    }
  };

  const halfW = width / 2;
  const halfL = length / 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '11.5px' }}>
      {/* Header Info */}
      <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            color: '#2F78C4',
            letterSpacing: '0.12em',
            marginBottom: '2px'
          }}
        >
          SPATIAL COMPUTATIONAL BOUNDS
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
            Domain Extents
          </div>
          <label
            htmlFor="domain-box-visibility-checkbox"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '2px 8px',
              borderRadius: '3px',
              border: showDomainBox ? '1px solid rgba(47, 120, 196, 0.4)' : '1px solid #cbd5e1',
              background: showDomainBox ? 'rgba(47, 120, 196, 0.08)' : '#f8fafc',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 600,
              color: showDomainBox ? '#2450A6' : '#64748b',
              userSelect: 'none'
            }}
            title="Toggle 3D domain bounding box visibility in viewport"
          >
            <input
              id="domain-box-visibility-checkbox"
              type="checkbox"
              checked={showDomainBox}
              onChange={(e) => dispatch(setShowDomainBox(e.target.checked))}
              style={{ cursor: 'pointer', margin: 0 }}
            />
            <span>Domain Box</span>
          </label>
        </div>
        <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>
          Scenario: <strong>{activeScenario?.name || 'Baseline'}</strong>
        </div>
      </div>

      {/* Domain Width (X) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label
          htmlFor="domain-width-input"
          style={{ fontWeight: 600, color: '#334155', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Domain Width (X)</span>
          <span style={{ fontFamily: 'ui-monospace, monospace', color: '#2450A6', fontWeight: 700 }}>
            {width} m
          </span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            id="domain-width-input"
            type="number"
            min="10"
            step="25"
            value={widthInput}
            onChange={handleWidthChange}
            onBlur={handleWidthBlur}
            style={{
              flex: 1,
              height: '28px',
              padding: '0 8px',
              borderRadius: '3px',
              border: '1px solid rgba(47, 120, 196, 0.25)',
              background: '#ffffff',
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              color: '#0f172a'
            }}
          />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '10px',
              padding: '4px 6px',
              background: 'rgba(47, 120, 196, 0.08)',
              borderRadius: '3px',
              color: '#475569',
              fontWeight: 600
            }}
          >
            metres (m)
          </span>
        </div>
        <span style={{ fontSize: '9.5px', color: '#64748b' }}>
          Cross-stream horizontal dimension along local X axis.
        </span>
      </div>

      {/* Domain Length (Z) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label
          htmlFor="domain-length-input"
          style={{ fontWeight: 600, color: '#334155', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Domain Length (Z)</span>
          <span style={{ fontFamily: 'ui-monospace, monospace', color: '#2450A6', fontWeight: 700 }}>
            {length} m
          </span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            id="domain-length-input"
            type="number"
            min="10"
            step="25"
            value={lengthInput}
            onChange={handleLengthChange}
            onBlur={handleLengthBlur}
            style={{
              flex: 1,
              height: '28px',
              padding: '0 8px',
              borderRadius: '3px',
              border: '1px solid rgba(47, 120, 196, 0.25)',
              background: '#ffffff',
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              color: '#0f172a'
            }}
          />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '10px',
              padding: '4px 6px',
              background: 'rgba(47, 120, 196, 0.08)',
              borderRadius: '3px',
              color: '#475569',
              fontWeight: 600
            }}
          >
            metres (m)
          </span>
        </div>
        <span style={{ fontSize: '9.5px', color: '#64748b' }}>
          Streamwise horizontal dimension along local Z axis.
        </span>
      </div>

      {/* Domain Height (Y) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label
          htmlFor="domain-height-input"
          style={{ fontWeight: 600, color: '#334155', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Domain Height (Y)</span>
          <span style={{ fontFamily: 'ui-monospace, monospace', color: '#2450A6', fontWeight: 700 }}>
            {height} m
          </span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            id="domain-height-input"
            type="number"
            min="10"
            step="20"
            value={heightInput}
            onChange={handleHeightChange}
            onBlur={handleHeightBlur}
            style={{
              flex: 1,
              height: '28px',
              padding: '0 8px',
              borderRadius: '3px',
              border: '1px solid rgba(47, 120, 196, 0.25)',
              background: '#ffffff',
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              color: '#0f172a'
            }}
          />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '10px',
              padding: '4px 6px',
              background: 'rgba(47, 120, 196, 0.08)',
              borderRadius: '3px',
              color: '#475569',
              fontWeight: 600
            }}
          >
            metres (m)
          </span>
        </div>
        <span style={{ fontSize: '9.5px', color: '#64748b' }}>
          Vertical dimension extending from ground level Y = 0.0 m upward.
        </span>
      </div>

      {/* Spatial Extents Summary Box */}
      <div
        style={{
          border: '1px solid rgba(47, 120, 196, 0.18)',
          borderRadius: '4px',
          background: 'rgba(248, 246, 241, 0.85)',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}
      >
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#475569'
          }}
        >
          CALCULATED SPATIAL ENVELOPE
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#334155' }}>
          <span>Total Domain Volume:</span>
          <strong style={{ fontFamily: 'ui-monospace, monospace' }}>
            {volume.toLocaleString()} m³
          </strong>
        </div>

        <div style={{ fontSize: '10px', fontFamily: 'ui-monospace, monospace', color: '#64748b' }}>
          <div>X Bounds: [-{halfW.toFixed(0)}, +{halfW.toFixed(0)}] m</div>
          <div>Y Bounds: [0.0, {height.toFixed(0)}] m (Ground = 0.0m)</div>
          <div>Z Bounds: [-{halfL.toFixed(0)}, +{halfL.toFixed(0)}] m</div>
        </div>
      </div>

      {/* Domain Visualization Indicator */}
      <div
        style={{
          borderLeft: '3px solid #2F78C4',
          padding: '6px 10px',
          background: 'rgba(47, 120, 196, 0.05)',
          borderRadius: '0 3px 3px 0',
          fontSize: '10px',
          color: '#334155',
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: '2px' }}>
          Viewport Representation
        </div>
        <div>
          The computational domain is visually rendered as a transparent volume with outlined edges in the 3D viewport.
          This is geometric domain framing only; no numerical mesh is generated in Gate 1.
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '6px',
            paddingTop: '5px',
            borderTop: '1px solid rgba(47, 120, 196, 0.12)',
            fontSize: '10px'
          }}
        >
          <span style={{ color: '#64748b' }}>Domain Box Status:</span>
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontWeight: 700,
              color: showDomainBox ? '#16a34a' : '#dc2626'
            }}
          >
            {showDomainBox ? '● VISIBLE' : '○ HIDDEN'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DomainInspector;
